// ============================================================================
// SubscriptionManager - Handles Real-time Updates via Supabase
// ============================================================================
// Recovery: when the WebSocket fails, ALL channels break together. Rather
// than patching them individually (which leaks orphans and triggers rate
// limits), we do an atomic reset: teardown everything → clear the channel
// list → recreate with staggering. The WebSocket is left alive so new
// channels subscribe immediately; if it's already dead, the first
// channel.subscribe() reconnects it automatically.
//
// Timeline: error → 3s wait → full reset →
// exponential backoff if still failing (6s → 12s → 24s → 48s → 60s cap).
// Safety nets: 5-min periodic health check + tab-focus check.
// ============================================================================

import { supabase } from 'app/auth/auth.js';

const HEALTH_CHECK_INTERVAL = 5 * 60 * 1000; // 5 min safety net
const RECOVERY_DEBOUNCE = 5000;  // 5s stable before clearing degraded
const RESET_DELAY_INITIAL = 3000;  // First reset attempt after 3s
const RESET_DELAY_MAX = 60000;     // Cap at 60s
const STAGGER_DELAY = 300;        // 300ms between each channel creation

class SubscriptionManager {
    constructor() {
        this.channels = new Map();       // key → RealtimeChannel
        this.callbacks = new Map();      // key → Set<callback>
        this.channelConfigs = new Map(); // key → { table, filter }
        this._healthInterval = null;
        this._connectionCallback = null;
        this._degraded = false;
        this._recoveryTimer = null;
        this._resetTimer = null;
        this._resetDelay = RESET_DELAY_INITIAL;
        this._resetting = false;
        this._channelSeq = 0;            // monotonic - guarantees unique channel names
    }

    /**
     * Register a callback for connection state changes.
     * Called with (connected: boolean) when subscription health changes.
     */
    setConnectionCallback(callback) {
        this._connectionCallback = callback;
    }

    /**
     * Subscribe to real-time changes on a table.
     * Returns an unsubscribe function.
     */
    subscribe(table, filter, callback) {
        const key = this.getKey(table, filter);

        if (!this.callbacks.has(key)) {
            this.callbacks.set(key, new Set());
        }
        this.callbacks.get(key).add(callback);

        if (!this.channels.has(key)) {
            this.channelConfigs.set(key, { table, filter });
            this._createChannel(key, table, filter);
            this._ensureHealthCheck();
        }

        return () => {
            const callbacks = this.callbacks.get(key);
            if (callbacks) {
                callbacks.delete(callback);

                if (callbacks.size === 0) {
                    this.callbacks.delete(key);
                    this.channelConfigs.delete(key);
                    const channel = this.channels.get(key);
                    if (channel) {
                        this.channels.delete(key);
                        supabase.removeChannel(channel);
                    }
                    if (this.channels.size === 0) this._stopHealthCheck();
                }
            }
        };
    }

    /**
     * Create a channel with status monitoring.
     * Any error schedules a full reset rather than per-channel patching.
     */
    _createChannel(key, table, filter) {
        // The name MUST be globally unique. supabase.channel(name) dedupes by
        // topic name, so two subscriptions sharing a name collapse onto ONE
        // channel and stack conflicting postgres_changes bindings - the server
        // then returns fewer bindings than the client expects and the channel
        // dies with "mismatch between server and client bindings". A bare
        // `sm-${table}-${Date.now()}` collides whenever two subscriptions to the
        // same table are created in the same millisecond (e.g. the task page
        // subscribes to tasks by id AND by client_id in one synchronous tick).
        // The monotonic counter makes every channel name unique regardless.
        const channelName = `sm-${table}-${Date.now()}-${++this._channelSeq}`;
        const pgConfig = { event: '*', schema: 'public', table };
        if (filter) pgConfig.filter = filter;

        const channel = supabase
            .channel(channelName)
            .on('postgres_changes', pgConfig, (payload) => this.handleChange(key, payload))
            .subscribe((status, err) => {
                // Ignore callbacks from replaced/removed channels
                if (this.channels.get(key) !== channel) return;

                if (status === 'SUBSCRIBED') {
                    this._onChannelRecovered();
                } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
                    console.warn(`[SubscriptionManager] Channel ${key} ${status}`, err || '');
                    this._setDegraded(true);
                    this._scheduleReset();
                }
            });

        this.channels.set(key, channel);
    }

    // ── Recovery ─────────────────────────────────────────────────────────

    /**
     * Schedule a full reset with exponential backoff.
     * Multiple error callbacks coalesce into a single timer, giving
     * Supabase's internal reconnect a window before we intervene.
     */
    _scheduleReset() {
        if (this._resetTimer || this._resetting) return;

        this._resetTimer = setTimeout(async () => {
            this._resetTimer = null;
            await this._resetAll();

            // Back off for next attempt if still degraded
            if (this._degraded) {
                this._resetDelay = Math.min(this._resetDelay * 2, RESET_DELAY_MAX);
            }
        }, this._resetDelay);
    }

    /**
     * Atomic reset: tear down everything and recreate from scratch.
     *
     * Why nuclear instead of per-channel?
     *  - supabase.removeChannel() silently leaks when WebSocket is down
     *    (only removes from internal list if unsubscribe returns 'ok')
     *  - channel.unsubscribe() doesn't stop the internal rejoinTimer
     *    (only teardown() does), so orphans keep trying to rejoin
     *  - Per-channel reconnects create a thundering herd that triggers
     *    Supabase's "Too many channels" rate limit
     *
     * The fix: teardown() every channel (stops timers), clear the channel
     * list, then recreate with staggering. We deliberately do NOT call
     * disconnect() - the WebSocket stays alive so new channels can
     * subscribe immediately. If the WebSocket is already dead, the first
     * channel.subscribe() triggers a fresh connection automatically.
     *
     * Note: we access supabase.realtime.channels (a public property on
     * RealtimeClient, returned by getChannels()) to clear the list.
     * removeAllChannels() would also call disconnect(), which creates a
     * race condition: conn.close() fires onclose asynchronously, scheduling
     * a reconnect that overwrites the connection our new channels just opened.
     */
    async _resetAll() {
        if (this._resetting) return;
        this._resetting = true;

        console.warn('[SubscriptionManager] Full reset: tearing down all channels');

        // 1. Leave + teardown() every channel Supabase knows about.
        //    teardown() alone only cleans up CLIENT state (rejoinTimers, pending
        //    pushes, bindings) - it never sends phx_leave, so the SERVER keeps
        //    the topic alive until the socket drops. Because we recreate with
        //    fresh names (below), the orphaned server channels pile up on the
        //    live socket and eventually trip Supabase's "Too many channels"
        //    limit. unsubscribe() sends the leave so the server releases each
        //    topic; we then teardown() to stop the rejoin timers. The leave is
        //    a lightweight message (not a re-join), so it does not contribute to
        //    join rate limits. We iterate getChannels() to catch orphans too.
        const socketLive = supabase.realtime.isConnected();
        for (const ch of supabase.getChannels()) {
            try {
                if (socketLive && (ch.state === 'joined' || ch.state === 'joining')) {
                    ch.unsubscribe();
                }
                ch.teardown();
            } catch { /* ignore */ }
        }

        // 2. Clear Supabase's internal channel list. Do NOT call disconnect()
        //    or removeAllChannels() - we want the WebSocket to stay alive so
        //    new channels can subscribe immediately without a reconnect race.
        supabase.realtime.channels = [];

        // 3. Clear our tracked channel refs (configs + callbacks survive)
        this.channels.clear();

        // 4. Refresh the auth token before recreating channels.
        //    When the JWT has expired, recreating channels with the stale token
        //    causes an infinite error/reset loop. getSession() triggers the
        //    autoRefreshToken mechanism and returns a fresh token.
        try {
            const { data, error } = await supabase.auth.getSession();
            if (error || !data?.session) {
                console.warn('[SubscriptionManager] Session refresh failed, aborting reset');
                this._resetting = false;
                return;
            }
            // Update the realtime client's access token so new channels use it
            supabase.realtime.setAuth(data.session.access_token);
        } catch (e) {
            console.warn('[SubscriptionManager] Token refresh error:', e);
            this._resetting = false;
            return;
        }

        // 5. Recreate all channels from saved configs, staggered to avoid rate limits
        const entries = [...this.channelConfigs.entries()];
        entries.forEach(([key, config], i) => {
            setTimeout(() => {
                // Guard: config may have been removed if component unsubscribed during reset
                if (!this.channelConfigs.has(key)) return;
                this._createChannel(key, config.table, config.filter);
            }, i * STAGGER_DELAY);
        });

        // Allow next reset after all staggered creates have fired
        const totalStaggerTime = entries.length * STAGGER_DELAY;
        setTimeout(() => { this._resetting = false; }, totalStaggerTime);
    }

    /**
     * When a channel recovers, debounce the recovery notification.
     * Only clears degraded after ALL channels stay healthy for 5s.
     */
    _onChannelRecovered() {
        if (!this._degraded) return;

        clearTimeout(this._recoveryTimer);
        this._recoveryTimer = setTimeout(() => {
            for (const channel of this.channels.values()) {
                const s = channel.state;
                if (s === 'errored' || s === 'closed') return; // Still degraded
            }
            this._setDegraded(false);
            this._resetDelay = RESET_DELAY_INITIAL; // Reset backoff on full recovery
        }, RECOVERY_DEBOUNCE);
    }

    // ── Health check (safety net) ────────────────────────────────────────

    /**
     * Periodic safety net: if any channels are still dead, schedule a reset.
     * The fast retry handles most cases; this catches anything it missed.
     */
    _healthCheck() {
        for (const channel of this.channels.values()) {
            if (channel.state === 'errored' || channel.state === 'closed') {
                this._scheduleReset();
                return;
            }
        }
        if (this._degraded) this._onChannelRecovered();
    }

    _ensureHealthCheck() {
        if (this._healthInterval) return;
        this._healthInterval = setInterval(() => this._healthCheck(), HEALTH_CHECK_INTERVAL);

        if (!this._onVisibilityChange) {
            this._lastVisibilityCheck = 0;
            this._onVisibilityChange = () => {
                if (document.visibilityState !== 'visible') return;
                const now = Date.now();
                if (now - this._lastVisibilityCheck < 30000) return;
                this._lastVisibilityCheck = now;
                this._healthCheck();
            };
            document.addEventListener('visibilitychange', this._onVisibilityChange);
        }

        if (!this._onBeforeUnload) {
            this._onBeforeUnload = () => this.cleanup();
            window.addEventListener('beforeunload', this._onBeforeUnload);
        }
    }

    _stopHealthCheck() {
        clearInterval(this._healthInterval);
        this._healthInterval = null;
        if (this._onVisibilityChange) {
            document.removeEventListener('visibilitychange', this._onVisibilityChange);
            this._onVisibilityChange = null;
        }
        if (this._onBeforeUnload) {
            window.removeEventListener('beforeunload', this._onBeforeUnload);
            this._onBeforeUnload = null;
        }
    }

    // ── Shared helpers ───────────────────────────────────────────────────

    _setDegraded(degraded) {
        if (this._degraded === degraded) return;
        this._degraded = degraded;
        if (degraded) clearTimeout(this._recoveryTimer);
        this._connectionCallback?.(!degraded);
    }

    handleChange(key, payload) {
        const callbacks = this.callbacks.get(key);
        if (callbacks) {
            callbacks.forEach(callback => {
                try {
                    callback(payload);
                } catch (error) {
                    console.error('[SubscriptionManager] Callback error:', error);
                }
            });
        }
    }

    getKey(table, filter) {
        return `${table}:${filter || 'all'}`;
    }

    cleanup() {
        this._stopHealthCheck();
        clearTimeout(this._recoveryTimer);
        clearTimeout(this._resetTimer);
        this._resetTimer = null;
        this._resetDelay = RESET_DELAY_INITIAL;
        this._resetting = false;
        for (const ch of supabase.getChannels()) {
            try { ch.teardown(); } catch { /* ignore */ }
        }
        supabase.realtime.channels = [];
        this.channels.clear();
        this.callbacks.clear();
        this.channelConfigs.clear();
        this._degraded = false;
    }
}

// Export singleton for global connection monitoring
export const globalSubscriptionManager = new SubscriptionManager();

// Debug helpers - access from DevTools console:
//   __sm.kill()          → close WebSocket (simulate disconnect)
//   __sm.channels()      → number of channels Supabase knows about
//   __sm.status()        → { degraded, tracked, internal, resetDelay }
if (typeof window !== 'undefined') {
    window.__sm = {
        kill: () => supabase.realtime.conn?.close(),
        channels: () => supabase.getChannels().length,
        status: () => ({
            degraded: globalSubscriptionManager._degraded,
            tracked: globalSubscriptionManager.channels.size,
            internal: supabase.getChannels().length,
            resetDelay: globalSubscriptionManager._resetDelay,
        }),
    };
}
